import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import {
  Eye, Download, Heart, HelpCircle, Check, ArrowRight, ArrowUpRight, Loader2, ShieldCheck,
} from "lucide-react";
import { CASE_STUDIES_FULL } from "../../data/caseStudies";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

/**
 * "Where is your app leaking growth?" — a 30-second self-diagnosis that
 * turns the hero funnel into a lead. Visitor picks the stage they're stuck
 * at, sees the likely causes + the fix + a matching case study, and can ask
 * for an audit. Leads post to /api/leads with source "leak-check".
 */

// Proof = first case study that used the relevant service.
const proofFor = (service) => CASE_STUDIES_FULL.find((c) => c.services.includes(service));

const STAGES = [
  {
    key: "awareness",
    color: "#2196F3",
    icon: Eye,
    title: "Nobody finds it",
    sub: "Low impressions · weak keyword ranks",
    causes: [
      "Title and subtitle don't carry the keywords people actually search",
      "Thin keyword coverage — ranking for your brand name only",
      "No off-store demand: no creators, press or content pointing at the listing",
    ],
    fix: { name: "App Store Optimization", to: "/aso" },
    also: { name: "Influencer marketing", to: "/influencer-marketing" },
    proof: proofFor("ASO"),
  },
  {
    key: "acquisition",
    color: "#EF5F5F",
    icon: Download,
    title: "Views don't become installs",
    sub: "Traffic, but weak conversion · high CPI",
    causes: [
      "Screenshots show features instead of outcomes — visitors don't see the 'why'",
      "Paid campaigns send users to one generic page, not a matching product page",
      "No conversion testing, so the page never improves",
    ],
    fix: { name: "User acquisition", to: "/user-acquisition" },
    also: { name: "Creative production", to: "/creative-production" },
    proof: proofFor("Paid Social"),
  },
  {
    key: "retention",
    color: "#F2A93B",
    icon: Heart,
    title: "Users install, then leave",
    sub: "Weak D7 / D30 · ratings slipping",
    causes: [
      "Onboarding asks for too much before showing the first win",
      "No lifecycle messaging — silent after day 1",
      "Unanswered 1–3★ reviews dragging the rating and the conversion down",
    ],
    fix: { name: "App retention", to: "/app-retention" },
    also: { name: "Ratings & reviews", to: "/ratings-reviews" },
    proof: proofFor("App Retention"),
  },
  {
    key: "unsure",
    color: "#2D2D2D",
    icon: HelpCircle,
    title: "Not sure where",
    sub: "Let the audit find it",
    causes: [
      "Most apps leak at two stages at once — the audit scores all of them",
      "You get a 0–100 ASO health score plus the three biggest gaps",
      "Takes about 30 seconds, no card required",
    ],
    fix: { name: "Free ASO audit", to: "/free-aso-audit" },
    also: { name: "Build your plan", to: "/build-your-plan" },
    proof: proofFor("ASO"),
  },
];

const platformFromUrl = (s = "") =>
  /apps\.apple\.com/i.test(s) ? "ios" : /play\.google\.com/i.test(s) ? "android" : undefined;

export default function GrowthLeakCheck() {
  const [stageKey, setStageKey] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", app: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | done
  const stage = STAGES.find((s) => s.key === stageKey);

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    const isUrl = /^https?:\/\//i.test(form.app.trim());
    try {
      const res = await fetch(`${API}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          app_name: isUrl ? undefined : form.app.trim(),
          app_store_url: isUrl ? form.app.trim() : undefined,
          platform: platformFromUrl(form.app),
          source: "leak-check",
          message: `Leak check — stuck at: ${stage.title}. Wants help with ${stage.fix.name}.`,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus("done");
    } catch {
      setStatus("idle");
      toast.error("Couldn't send that — please try again or email hello@appfillip.com");
    }
  };

  return (
    <section className="py-16 md:py-20 bg-white border-b border-zinc-200" data-testid="leak-check">
      <div className="container">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Intro */}
          <div className="lg:col-span-4">
            <p className="text-overline text-electric mb-3">30-second check</p>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-charcoal text-balance leading-[1.05]">
              Where is your app leaking growth?
            </h2>
            <p className="mt-4 text-zinc-600 leading-relaxed">
              Every app loses users at one of three places. Pick the one that sounds like
              yours and we&apos;ll show the usual causes, the fix, and an app we fixed it for.
            </p>
            <p className="mt-4 inline-flex items-center gap-1.5 text-xs text-zinc-500">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> No sign-up to see the diagnosis
            </p>
          </div>

          {/* Choices + diagnosis */}
          <div className="lg:col-span-8">
            <div className="grid grid-cols-2 gap-3" role="group" aria-label="Where is your app stuck?">
              {STAGES.map((s) => {
                const Icon = s.icon;
                const active = s.key === stageKey;
                return (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => {
                      setStageKey(s.key);
                      setStatus("idle");
                    }}
                    aria-pressed={active}
                    data-testid={`leak-choice-${s.key}`}
                    className={`text-left border p-4 md:p-5 transition-all hover:-translate-y-0.5 ${
                      active ? "border-charcoal bg-white" : "border-zinc-200 bg-zinc-50 hover:bg-white hover:border-zinc-400"
                    }`}
                    style={active ? { boxShadow: `4px 4px 0 0 ${s.color}` } : undefined}
                  >
                    <span className="inline-grid place-items-center w-8 h-8 mb-3" style={{ backgroundColor: `${s.color}1a`, color: s.color }}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <span className="block font-display font-bold text-charcoal leading-snug">{s.title}</span>
                    <span className="block mt-1 text-xs text-zinc-500">{s.sub}</span>
                  </button>
                );
              })}
            </div>

            <AnimatePresence mode="wait">
              {stage && (
                <motion.div
                  key={stage.key}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.3 }}
                  className="mt-4 border border-charcoal bg-white"
                  data-testid="leak-diagnosis"
                >
                  <div className="grid md:grid-cols-2">
                    {/* Causes + fix */}
                    <div className="p-5 md:p-6 border-b md:border-b-0 md:border-r border-zinc-200">
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-3" style={{ color: stage.color }}>
                        Usual causes
                      </p>
                      <ul className="space-y-2.5">
                        {stage.causes.map((c) => (
                          <li key={c} className="flex items-start gap-2 text-[13px] text-zinc-700 leading-snug">
                            <Check className="w-3.5 h-3.5 mt-0.5 flex-none" style={{ color: stage.color }} />
                            {c}
                          </li>
                        ))}
                      </ul>

                      <p className="text-[10px] font-black uppercase tracking-[0.2em] mt-5 mb-2 text-zinc-500">The fix</p>
                      <div className="flex flex-wrap gap-2">
                        <Link to={stage.fix.to} className="inline-flex items-center gap-1.5 bg-charcoal text-white px-3 py-2 text-xs font-bold hover:bg-electric transition-colors">
                          {stage.fix.name} <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                        <Link to={stage.also.to} className="inline-flex items-center gap-1.5 border border-zinc-300 text-charcoal px-3 py-2 text-xs font-bold hover:border-charcoal transition-colors">
                          {stage.also.name}
                        </Link>
                      </div>

                      {stage.proof && (
                        <Link
                          to={`/case-studies/${stage.proof.slug}`}
                          className="group mt-5 flex items-center gap-3 border border-zinc-200 p-3 hover:border-charcoal transition-colors"
                          data-testid="leak-proof"
                        >
                          <span className="font-display text-2xl font-black leading-none" style={{ color: stage.color }}>
                            {stage.proof.metric}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-[13px] font-bold text-charcoal truncate">
                              {stage.proof.metricLabel} · {stage.proof.appName}
                            </span>
                            <span className="block text-[11px] text-zinc-500">We fixed exactly this — read the case study</span>
                          </span>
                          <ArrowUpRight className="w-4 h-4 text-zinc-400 group-hover:text-electric flex-none" />
                        </Link>
                      )}
                    </div>

                    {/* Ask for an audit */}
                    <div className="p-5 md:p-6 bg-zinc-50">
                      {status === "done" ? (
                        <div className="h-full flex flex-col justify-center" data-testid="leak-success">
                          <span className="inline-grid place-items-center w-10 h-10 bg-emerald-500 text-white mb-3">
                            <Check className="w-5 h-5" />
                          </span>
                          <h3 className="font-display text-xl font-bold text-charcoal">Got it — audit requested.</h3>
                          <p className="mt-2 text-sm text-zinc-600 leading-relaxed">
                            We&apos;ll look at {/^https?:\/\//i.test(form.app.trim()) ? "your app" : form.app.trim() || "your app"} and reply to {form.email.trim()} within one
                            business day with the {stage.fix.name.toLowerCase()} gaps we&apos;d fix first.
                          </p>
                          <Link to="/free-aso-audit" className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-electric hover:text-charcoal">
                            Can&apos;t wait? Run the instant audit <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                        </div>
                      ) : (
                        <form onSubmit={submit} className="h-full flex flex-col" data-testid="leak-form">
                          <h3 className="font-display text-lg font-bold text-charcoal leading-snug">
                            Want us to check {stage.key === "unsure" ? "all three" : "this"} for your app?
                          </h3>
                          <p className="mt-1 text-xs text-zinc-500">Free, human-reviewed. Reply within one business day.</p>
                          <div className="mt-4 space-y-2.5">
                            <input
                              required
                              value={form.app}
                              onChange={(e) => setForm({ ...form, app: e.target.value })}
                              placeholder="App name or store link"
                              aria-label="App name or store link"
                              className="w-full border border-zinc-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-electric"
                            />
                            <div className="grid grid-cols-2 gap-2.5">
                              <input
                                required
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Your name"
                                aria-label="Your name"
                                autoComplete="name"
                                className="w-full border border-zinc-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-electric"
                              />
                              <input
                                required
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                placeholder="Work email"
                                aria-label="Work email"
                                autoComplete="email"
                                className="w-full border border-zinc-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-electric"
                              />
                            </div>
                          </div>
                          <button
                            type="submit"
                            disabled={status === "sending"}
                            data-testid="leak-submit"
                            className="mt-3 inline-flex items-center justify-center gap-2 bg-electric text-white px-5 py-3 text-sm font-bold hover:bg-electric-400 transition-colors disabled:opacity-60 shadow-[3px_3px_0_0_#2D2D2D]"
                          >
                            {status === "sending" ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                            Request my free check
                          </button>
                          <p className="mt-auto pt-3 text-[11px] text-zinc-500">
                            Or run the <Link to="/free-aso-audit" className="text-electric font-semibold hover:underline">instant ASO audit</Link> — scored in 30 seconds.
                          </p>
                        </form>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
